import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Pagination,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, FilterList as FilterIcon, Search as SearchIcon } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import api from '../services/api';
import { apoliceService, Apolice, CreateApoliceDTO } from '../services/apoliceService';
import { pagamentoService, Pagamento, CreatePagamentoDTO } from '../services/pagamentoService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Cliente } from '../services/clienteService';
import { useAuthContext } from '../contexts/AuthContext';
import ApoliceFormModal from '../components/ApoliceFormModal';

interface Ramo {
  id: number;
  nome: string;
}

interface Seguradora {
  id: number;
  nome: string;
}

const GerenciarApolicesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthContext();
  const [apolicesFiltradas, setApolicesFiltradas] = useState<Apolice[]>([]);
  const [loading, setLoading] = useState(false);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [loadingRamos, setLoadingRamos] = useState(false);
  const [seguradoras, setSeguradoras] = useState<Seguradora[]>([]);
  const [loadingSeguradoras, setLoadingSeguradoras] = useState(false);
  
  // Filtros
  const [filtroRamo, setFiltroRamo] = useState<string>('');
  const [filtroSeguradora, setFiltroSeguradora] = useState<string>('');
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [filtroDataInicio, setFiltroDataInicio] = useState<string>('');
  const [filtroDataFim, setFiltroDataFim] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog
  const [open, setOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const carregarSeguradoras = async () => {
    setLoadingSeguradoras(true);
    try {
      const response = await api.get('/seguradoras');
      setSeguradoras(response.data);
    } catch (error) {
      console.error('Erro ao carregar seguradoras:', error);
    } finally {
      setLoadingSeguradoras(false);
    }
  };

  const carregarRamos = async () => {
    setLoadingRamos(true);
    try {
      const response = await api.get('/ramos');
      setRamos(response.data);
    } catch (error) {
      console.error('Erro ao carregar ramos:', error);
    } finally {
      setLoadingRamos(false);
    }
  };

  const carregarApolices = async (pagina = 1) => {
   
    setLoading(true);
    try {
      const filtros = {
        search: searchTerm,
        ramo: filtroRamo,
        seguradora: filtroSeguradora,
        status: filtroStatus,
        dataInicio: filtroDataInicio,
        dataFim: filtroDataFim,
      };

      const result = await apoliceService.listar(filtros, pagina, limit);

      setApolicesFiltradas(result.data);
      setTotal(result.total);
      setPage(result.page);
      setLimit(result.limit);
    } catch (error:any) {
      console.log('Erro ao carregar apólices:', error);
      toast.error('Erro ao carregar apólices:'+error.message);
    } finally {
      
      setLoading(false);
    }
  };

  const limparFiltros = () => {
    setFiltroRamo('');
    setFiltroSeguradora('');
    setFiltroStatus('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setSearchTerm('');
    navigate('/apolices'); // Limpa a URL e dispara a busca
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta apólice?')) {
      try {
        await apoliceService.excluir(id);
        toast.success('Apólice excluída com sucesso!');
        carregarApolices(page);
      } catch (error) {
        console.error('Erro ao excluir apólice:', error);
        toast.error('Erro ao excluir apólice. Por favor, tente novamente.');
      }
    }
  };

  const handleEdit = (apolice: Apolice) => {
    navigate(`/apolices/editar/${apolice.id}`);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1); // Reseta para a primeira página ao buscar
    carregarApolices(1);
  };

  useEffect(() => {
    carregarRamos();
    carregarSeguradoras();

    // Ler filtros da URL na montagem inicial
    const params = new URLSearchParams(window.location.search);
    const statusUrl = params.get('status');
    if (statusUrl) {
      setFiltroStatus(statusUrl);
    }
  }, []);

  useEffect(() => {
    // Este useEffect reage a qualquer mudança nos filtros ou na página
    // e chama a função de carregar apólices.
    carregarApolices(page);
  }, [page, limit, filtroRamo, filtroSeguradora, filtroStatus, filtroDataInicio, filtroDataFim, searchTerm]);

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativa': return 'success';
      case 'Cancelada': return 'error';
      case 'Vencida': return 'warning';
      case 'Renovação': return 'info';
      default:
        return 'default';
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Apólices
          </Typography>

          {hasPermission({ entidade: 'apolice', acao: 'can_create' }) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />} 
              onClick={() => setOpen(true)}
              sx={{ mb: 3 }}
            >
              Nova Apólice
            </Button>
          )}

          {/* Filtros */}
          <Paper sx={{ p: 2, mb: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Filtros
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 12 }}>
                <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por nº apólice, cliente, seguradora ou placa"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon /></InputAdornment>
                      )
                    }}
                  />
                  <Button type="submit" variant="contained">Buscar</Button>
                </form>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Ramo</InputLabel>
                  <Select
                    value={filtroRamo}
                    label="Ramo"
                    onChange={(e) => setFiltroRamo(e.target.value)}
                    disabled={loadingRamos}
                  >
                    <MenuItem value="">Todos os ramos</MenuItem>
                    {ramos.map((ramo) => (
                      <MenuItem key={ramo.id} value={ramo.nome}>
                        {ramo.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Seguradora</InputLabel>
                  <Select
                    value={filtroSeguradora}
                    label="Seguradora"
                    onChange={(e) => setFiltroSeguradora(e.target.value)}
                    disabled={loadingSeguradoras}
                  >
                    <MenuItem value="">Todas as seguradoras</MenuItem>
                    {seguradoras.map((seguradora) => (
                      <MenuItem key={seguradora.id} value={seguradora.nome}>
                        {seguradora.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filtroStatus}
                    label="Status"
                    onChange={(e) => setFiltroStatus(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Ativa">Ativa</MenuItem>
                    <MenuItem value="Cancelada">Cancelada</MenuItem>
                    <MenuItem value="Vencida">Vencida</MenuItem>
                    <MenuItem value="A_Vencer">A Vencer (30d)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Início"
                  value={filtroDataInicio}
                  onChange={(e) => setFiltroDataInicio(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Fim"
                  value={filtroDataFim}
                  onChange={(e) => setFiltroDataFim(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button onClick={limparFiltros} variant="outlined" fullWidth>Limpar Filtros</Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 'bold', textTransform: 'uppercase', bgcolor: 'grey.100' } }}>
                    <TableCell>Número</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Seguradora</TableCell>
                    <TableCell>Ramo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Início</TableCell>
                    <TableCell>Fim</TableCell>
                    <TableCell align="center" sx={{ width: '120px' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : (
                    apolicesFiltradas.map((apolice) => (
                      <TableRow key={apolice.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>{apolice.numero}</TableCell>
                        <TableCell>{apolice.cliente?.nome || '-'}</TableCell>
                        <TableCell>{apolice.seguradora?.nome || '-'}</TableCell>
                        <TableCell>{apolice.ramo?.nome || '-'}</TableCell>
                        <TableCell>
                          <Chip label={apolice.status} color={getStatusColor(apolice.status)} size="small" sx={{ fontWeight: 'bold' }} />
                        </TableCell>
                        <TableCell>{formatarData(apolice.dataInicio)}</TableCell>
                        <TableCell>{formatarData(apolice.dataFim)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            {hasPermission({ entidade: 'apolice', acao: 'can_update' }) && (
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(apolice)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission({ entidade: 'apolice', acao: 'can_delete' }) && (
                              <Tooltip title="Excluir">
                                <IconButton size="small" color="error" onClick={() => handleDelete(apolice.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="select-limit-label">Tamanho da página</InputLabel>
                <Select
                  labelId="select-limit-label"
                  value={limit}
                  label="Tamanho da página"
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
              <Pagination
                count={Math.ceil(total / limit)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          </Paper>

          <ApoliceFormModal
            open={open}
            onClose={handleCloseDialog}
            onSave={() => carregarApolices(page)}
          />
        </Box>
      </Container>
    </MainLayout>
  );
};

export default GerenciarApolicesPage; 